/**
 * AudiobookTableV2 — Audiobooks list, ported from the prototype's
 * `.screen-audiobooks` (cms-redesign-mockup.html ~line 6137+, card grid
 * styles 1291–1403, table-alt styles 2280–2291).
 *
 * Layout:
 *   • Toolbar — status SegmentedControl (All / Active / Inactive with
 *     counts) on the left, Cards/Table view toggle on the right.
 *   • Card grid (default) — `.audiobook-grid` repeat(auto-fill, minmax(220px,1fr))
 *     of `.ab-card`s with 3:4 cover + title + sessions count + authors meta.
 *     Hover lifts the card and switches the border to teal.
 *   • Compact table view — alternate `.audiobook-table-view` with the
 *     same data laid out as a tight LibTable.
 *
 * Data source: GET /subcategories?is_audiobook=1 (legacy `{objects, count}`
 * envelope, enriched after #560/#564 with `subcategory_image`, `authors[]`,
 * `chapter_count`, and `session_count`). We reuse the v1
 * `fetchAllSubCategories(1)` action so Redux state stays shared between
 * v1 / v2.
 *
 * No paginated mode on this endpoint today — there are only a few
 * audiobooks (single-digit count for the foreseeable future) so we
 * render the full list and skip the pager. The prototype's `.list-foot`
 * pagination block is intentionally omitted for the same reason.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchAllSubCategories,
  clearAllSubCategories,
} from '../../../actions/subcategoryActions';
import {
  LibTable,
  SegmentedControl,
  IconButton,
  StatusEdge,
  cn,
} from '../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const CardsViewIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const TableViewIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="4" y1="6"  x2="20" y2="6"  />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

// Initials for the avatar bubble — first letter of first + last word.
// "Nick Ortner" → "NO", "Lulu" → "LU".
function initials(name) {
  if (!name) return '··';
  const words = String(name).trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

// Cycle through the brand palette so multi-author cards have distinct
// avatar tints (matches the prototype's `.av.a-n`, `.a-j`, `.a-k`
// classes — teal / terracotta / sage in order).
const AVATAR_BG = ['#75A293', '#C4715B', '#9DA67E'];
function avatarBgFor(index, author) {
  if (author?.author_image) return undefined; // image overrides solid
  return AVATAR_BG[index % AVATAR_BG.length];
}

/* ─── Author cluster (multi-author aware) ──────────────────────────── */
/*
 *  • 0 authors  → "—"
 *  • 1 author   → single avatar + full name
 *  • 2+ authors → stacked overlapping avatars + "N authors" label
 */
function AuthorCluster({ authors }) {
  if (!Array.isArray(authors) || authors.length === 0) {
    return <span className="text-text-muted">—</span>;
  }

  const showCount = authors.length > 1;
  // `.avatars` overlaps via negative margin-left (prototype: -6px).
  return (
    <span className="inline-flex items-center" style={{ gap: 6 }}>
      <span className="inline-flex" style={{ flexShrink: 0 }}>
        {authors.slice(0, 3).map((a, idx) => (
          <span
            key={a.author_id ?? `${a.author_name}-${idx}`}
            className="grid place-items-center font-condensed font-bold text-white"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              fontSize: 10.5,
              border: '1.5px solid #FAF7F1', // surface
              marginLeft: idx === 0 ? 0 : -7,
              background: avatarBgFor(idx, a),
              backgroundImage: a.author_image ? `url('${a.author_image}')` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            aria-hidden
          >
            {!a.author_image && initials(a.author_name)}
          </span>
        ))}
      </span>
      {showCount ? (
        <span className="text-text-light" style={{ fontWeight: 500 }}>
          {authors.length} authors
        </span>
      ) : (
        <span className="text-text" style={{ fontWeight: 500 }}>
          {authors[0].author_name}
        </span>
      )}
    </span>
  );
}

/* ─── Card ─────────────────────────────────────────────────────────── */
/*
 *  Mirrors `.ab-card` (prototype 1301+):
 *    • 3:4 cover with status pin
 *    • body: title (line-clamp 2) + meta line (chapters/sessions + authors)
 *    • hover lifts the card + tints border teal
 *    • inactive: grayscale cover + italic muted title + cream overlay
 */
function AudiobookCard({ ab }) {
  const active = !!ab.subcategory_is_active;
  const chapters = Number(ab.chapter_count ?? 0);
  const sessions = Number(ab.session_count ?? 0);
  const total = Number(ab.subcategory_num_sessions ?? chapters + sessions);

  // Only surface non-zero buckets so a chapters-only book doesn't read
  // "12 chapters · 0 sessions" (visually noisy) and a still-empty book
  // doesn't read "0 sessions". When both buckets are zero but we have
  // a legacy aggregate total, fall back to that; otherwise empty.
  const countParts = [];
  if (chapters > 0) countParts.push(`${chapters} chapter${chapters === 1 ? '' : 's'}`);
  if (sessions > 0) countParts.push(`${sessions} session${sessions === 1 ? '' : 's'}`);
  let countText = countParts.join(' · ');
  if (!countText && total > 0) {
    countText = `${total} session${total === 1 ? '' : 's'}`;
  }

  return (
    <Link
      to={`/audiobooks/edit/${ab.subcategory_id}`}
      className={cn(
        'flex flex-col bg-surface border border-sand overflow-hidden no-underline',
        'transition-all duration-200',
        'hover:border-teal-green',
        // Translate-y + shadow handled inline because the brand drop
        // shadow uses specific navy tints not in the Tailwind palette.
      )}
      style={{
        borderRadius: 12,
        color: 'inherit',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow =
          '0 10px 24px #2C3E5018, 0 2px 6px #2C3E500C';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Square cover (1:1). Prototype showed 3:4 (unsplash mocks were
       *  portrait), but the real audiobook cover assets are square,
       *  so we match the source-of-truth aspect. */}
      <div
        className="bg-cream"
        style={{
          aspectRatio: '1 / 1',
          backgroundImage: ab.subcategory_image
            ? `url('${ab.subcategory_image}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          // Inactive: gentle grayscale + cream haze (1397–1402).
          filter: active ? undefined : 'grayscale(40%) brightness(0.95)',
        }}
      >
        {/* `.ab-status-pin` (1324–1335). 10px dot with white ring. */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 10,
            height: 10,
            borderRadius: '50%',
            boxShadow: '0 0 0 3px #FFFFFFB3',
            background: active ? '#75A293' : '#9CA1A8',
          }}
        />
        {!active && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#FAF4EA77',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* `.ab-body` (1336–1370). */}
      <div
        className="flex flex-col"
        style={{ padding: '14px 16px 16px', gap: 4, flex: 1 }}
      >
        <div
          className={cn(
            'font-bold text-navy',
            !active && 'italic text-text-light',
          )}
          style={{
            fontSize: 15,
            letterSpacing: '-0.005em',
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {ab.subcategory_title || 'Untitled'}
        </div>

        {/* Two-line meta stack: authors first (primary identity),
         *  then the chapter/session breakdown beneath. The count line
         *  is suppressed entirely when there's nothing to show. */}
        <div
          className="flex flex-col"
          style={{ marginTop: 'auto', paddingTop: 10, gap: 4 }}
        >
          <div style={{ fontSize: 13 }}>
            <AuthorCluster authors={ab.authors} />
          </div>
          {countText && (
            <div className="text-text-light font-mono" style={{ fontSize: 11.5 }}>
              {countText}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ─── Table view ───────────────────────────────────────────────────── */
/*
 *  Alternate dense layout, toggled via the view-toggle. Same data as
 *  the card grid — Cover (36×48 thumb) + title + count + authors +
 *  hover-revealed Edit.
 */
function AudiobookTableRows({ items }) {
  // The 28px page gutter has to live on a wrapper, NOT as margin on
  // the table itself — LibTable applies `w-full` (width: 100%), so a
  // margin would make the total layout width `100% + 56px` and the
  // page would overflow horizontally.
  return (
    <div style={{ padding: '0 28px 32px' }}>
    <LibTable
      fixed
      className="border border-sand"
      style={{ borderRadius: 12, overflow: 'hidden' }}
    >
      <colgroup>
        <col style={{ width: 22 }} />
        <col style={{ width: 56 }} />
        <col />
        <col style={{ width: 70 }} />
        <col style={{ width: 70 }} />
        <col style={{ width: '22%' }} />
        <col style={{ width: 60 }} />
      </colgroup>
      <LibTable.Head>
        <tr>
          <LibTable.Th aria-label="Status" style={{ padding: 0 }}> </LibTable.Th>
          <LibTable.Th aria-label="Cover"> </LibTable.Th>
          <LibTable.Th>Audiobook</LibTable.Th>
          <LibTable.NumTh>Chapters</LibTable.NumTh>
          <LibTable.NumTh>Sessions</LibTable.NumTh>
          <LibTable.Th>Author(s)</LibTable.Th>
          <LibTable.Th aria-label="Actions"> </LibTable.Th>
        </tr>
      </LibTable.Head>
      <tbody>
        {items.map((ab) => {
          const active = !!ab.subcategory_is_active;
          const chapters = Number(ab.chapter_count ?? 0);
          const sessions = Number(ab.session_count ?? 0);
          // Legacy fallback: rows that pre-date the chapter/session
          // split only have `subcategory_num_sessions` as a flat total.
          // Show it in the Sessions column so the row isn't a wash of
          // zeros, and leave Chapters muted.
          const hasSplit = chapters + sessions > 0;
          const fallbackTotal = Number(ab.subcategory_num_sessions ?? 0);
          const sessionsCell = hasSplit ? sessions : fallbackTotal;
          return (
            <LibTable.Row key={ab.subcategory_id} inactive={!active}>
              {/* StatusEdge: thin colored bar on the leftmost edge. */}
              <td
                className="border-b border-line group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
                style={{ padding: 0, width: 22, position: 'relative' }}
              >
                <StatusEdge active={active} />
              </td>
              <LibTable.Td>
                <div
                  className="bg-cream border border-sand"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 4,
                    backgroundImage: ab.subcategory_image
                      ? `url('${ab.subcategory_image}')`
                      : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              </LibTable.Td>
              <LibTable.Td>
                {/* truncate + min-w-0 so a long title doesn't push the
                 *  declared column widths past the viewport (table-
                 *  layout: fixed still respects content min-width for
                 *  unbreakable strings). */}
                <Link
                  to={`/audiobooks/edit/${ab.subcategory_id}`}
                  className={cn(
                    'font-semibold no-underline text-navy hover:text-teal-green-dark',
                    'block truncate min-w-0',
                    !active && 'italic text-text-light',
                  )}
                  style={{ fontSize: 13.5 }}
                  title={ab.subcategory_title || 'Untitled'}
                >
                  {ab.subcategory_title || 'Untitled'}
                </Link>
              </LibTable.Td>
              <LibTable.NumTd muted={!chapters}>
                {chapters}
              </LibTable.NumTd>
              <LibTable.NumTd muted={!sessionsCell}>
                {sessionsCell}
              </LibTable.NumTd>
              <LibTable.Td>
                <AuthorCluster authors={ab.authors} />
              </LibTable.Td>
              <LibTable.Td style={{ textAlign: 'right' }}>
                <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  <Link
                    to={`/audiobooks/edit/${ab.subcategory_id}`}
                    aria-label={`Edit ${ab.subcategory_title}`}
                  >
                    <IconButton size="sm" aria-label="Edit">
                      {EditIconSvg}
                    </IconButton>
                  </Link>
                </div>
              </LibTable.Td>
            </LibTable.Row>
          );
        })}
      </tbody>
    </LibTable>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

const STATUS_FILTERS = ['all', 'active', 'inactive'];
const VIEW_MODES = ['grid', 'table'];

export default function AudiobookTableV2() {
  const dispatch = useDispatch();
  // The v1 reducer's `subcategories` slice is a map keyed by ID — we
  // share state so a flag-flip back to v1 doesn't refetch.
  const rawMap = useSelector((s) => s.subcategories.subcategories);
  const all = useMemo(
    () => (rawMap ? Object.values(rawMap) : null),
    [rawMap],
  );

  const [status, setStatus] = useState('all');
  const [view, setView] = useState('grid');

  useEffect(() => {
    dispatch(clearAllSubCategories());
    // `1` = is_audiobook flag → API returns the enriched audiobook
    // shape (image + authors + chapter_count + session_count).
    dispatch(fetchAllSubCategories(1));
  }, [dispatch]);

  // The endpoint already filters by `category_is_audiobook=1`, but the
  // legacy table also filters `subcategory_is_book=true` locally as a
  // belt-and-suspenders safety net; mirror that here in case any rows
  // leak through from the join.
  const audiobooks = useMemo(() => {
    if (!all) return null;
    return all.filter((s) => s.subcategory_is_book);
  }, [all]);

  // Status filter — applied client-side because the API doesn't expose
  // a status filter on this endpoint and the dataset is small.
  const visible = useMemo(() => {
    if (!audiobooks) return [];
    if (status === 'all') return audiobooks;
    if (status === 'active') return audiobooks.filter((a) => a.subcategory_is_active);
    return audiobooks.filter((a) => !a.subcategory_is_active);
  }, [audiobooks, status]);

  // Counts for the SegmentedControl badges. Recomputed on every
  // change but the list is tiny — cheap.
  const counts = useMemo(() => {
    if (!audiobooks) return { all: 0, active: 0, inactive: 0 };
    return audiobooks.reduce(
      (acc, a) => {
        acc.all += 1;
        if (a.subcategory_is_active) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { all: 0, active: 0, inactive: 0 },
    );
  }, [audiobooks]);

  if (!audiobooks) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading audiobooks…
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar — status SegmentedControl + view toggle. Same padding
       *  as the redesigned Sessions toolbar (0 28px). */}
      <div
        className="flex items-center justify-between"
        style={{ padding: '0 28px 16px', gap: 12 }}
      >
        <SegmentedControl
          value={status}
          onChange={setStatus}
          options={STATUS_FILTERS.map((v) => ({
            value: v,
            label: v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive',
            count: counts[v],
          }))}
        />
        <SegmentedControl
          shape="md"
          value={view}
          onChange={setView}
          options={[
            { value: 'grid',  label: 'Cards', icon: CardsViewIcon },
            { value: 'table', label: 'Table', icon: TableViewIcon },
          ]}
        />
      </div>

      {visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          {status === 'all'
            ? 'No audiobooks yet — create one to get started.'
            : `No ${status} audiobooks.`}
        </div>
      ) : view === 'grid' ? (
        // `.audiobook-grid` (1292–1300): repeat(auto-fill, minmax(220,1fr)).
        <div
          style={{
            padding: '0 28px 32px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 22,
            alignContent: 'start',
            background: 'transparent',
          }}
        >
          {visible.map((ab) => (
            <AudiobookCard key={ab.subcategory_id} ab={ab} />
          ))}
        </div>
      ) : (
        <AudiobookTableRows items={visible} />
      )}
    </div>
  );
}

